/**
 * Session `call-recipe` subcommand.
 *
 * Runs a property-scoped interaction recipe against a live browser session by
 * fetching the recipe, wrapping it in the recipe ABI, and dispatching through
 * the existing `run_code` daemon tool.
 */

import process from 'node:process';
import { getArgValue, hasFlag, resolveConfig } from '../auth.js';
import { apiRequest } from '../cli-helpers.js';
import { callTool, resolveTargetSession } from './daemon-client.js';
import type { ToolResponse } from './types.js';

/**
 * Compose a recipe's `({ page, args, vars, macro, attach }) => ...` body into a
 * self-contained `run_code` snippet. args/vars are inlined as JSON literals
 * (JSON is valid JS, so no injection surface); `macro` throws if touched (it
 * needs the server's generator) and `attach` is a no-op in an interactive
 * session. Exported for unit testing.
 */
export function buildRecipeRunCode(
  recipeCode: string,
  args: Record<string, unknown>,
  vars: Record<string, unknown>
): string {
  return [
    `const args = ${JSON.stringify(args)};`,
    `const vars = ${JSON.stringify(vars)};`,
    `const macro = new Proxy({}, { get() { throw new Error('macro is unavailable in an interactive session; pre-resolve dynamic values into --args'); } });`,
    `const attach = async () => {};`,
    `return await (${recipeCode})({ page, args, vars, macro, attach });`,
  ].join('\n');
}

type SessionRecipe = {
  slug: string;
  title: string;
  code: string;
  currentVersion: number;
  argsSchema: { required?: unknown };
};
type RecipeGetResponse = { ok: boolean; error?: string; data?: SessionRecipe };

export type PrintToolResult = (result: ToolResponse, jsonMode: boolean) => void;

/**
 * Run a property-scoped interaction recipe against the live session — the human
 * counterpart to the agent's `call_recipe`.
 *
 * In-session limitations (interactive context, not a workflow run): `args`/`vars`
 * are inlined from flags; `macro` is unavailable (pre-resolve dynamic values into
 * --args) and `attach` is a no-op. Recipes that only drive the page work as-is.
 */
export async function handleCallRecipe(argv: string[], printToolResult: PrintToolResult) {
  const jsonMode = hasFlag(argv, '--json');
  try {
    const { apiUrl, token } = await resolveConfig(argv);
    const target = await resolveTargetSession(getArgValue(argv, '--session'));

    const propertyId = getArgValue(argv, '--application') ?? getArgValue(argv, '--property');
    if (!propertyId) {
      console.error(
        'Error: --application <propertyId> is required (the property that owns the recipe).'
      );
      process.exitCode = 1;
      return;
    }
    const slug = getArgValue(argv, '--slug');
    if (!slug) {
      console.error('Error: --slug <slug> is required.');
      process.exitCode = 1;
      return;
    }

    let args: Record<string, unknown> = {};
    const argsRaw = getArgValue(argv, '--args');
    if (argsRaw) {
      try {
        args = JSON.parse(argsRaw) as Record<string, unknown>;
      } catch (error) {
        console.error(
          `Error: --args must be valid JSON (${error instanceof Error ? error.message : String(error)}).`
        );
        process.exitCode = 1;
        return;
      }
    }
    let vars: Record<string, unknown> = {};
    const varsRaw = getArgValue(argv, '--vars');
    if (varsRaw) {
      try {
        vars = JSON.parse(varsRaw) as Record<string, unknown>;
      } catch (error) {
        console.error(
          `Error: --vars must be valid JSON (${error instanceof Error ? error.message : String(error)}).`
        );
        process.exitCode = 1;
        return;
      }
    }

    const recipeResult = await apiRequest<RecipeGetResponse>(
      apiUrl,
      token,
      'GET',
      `/org/properties/${propertyId}/interaction-recipes/${encodeURIComponent(slug)}`
    );
    if (!recipeResult.ok || !recipeResult.data) {
      console.error(`Error: ${recipeResult.error ?? `recipe "${slug}" not found`}`);
      process.exitCode = 1;
      return;
    }
    const recipe = recipeResult.data;

    // Light required-args check (server ABI validates deeper at call time).
    const required: unknown[] = Array.isArray(recipe.argsSchema.required)
      ? recipe.argsSchema.required
      : [];
    const missing = required.filter(
      (name): name is string => typeof name === 'string' && !(name in args)
    );
    if (missing.length) {
      console.error(`Error: recipe "${slug}" requires args: ${missing.join(', ')}.`);
      process.exitCode = 1;
      return;
    }

    const wrappedCode = buildRecipeRunCode(recipe.code, args, vars);
    const observe = getArgValue(argv, '--observe') ?? 'settle';
    const result = await callTool(target.id, 'run_code', { code: wrappedCode, observe });
    if (!jsonMode && result.ok) {
      console.log(`Ran recipe "${recipe.slug}" v${recipe.currentVersion}.`);
    }
    printToolResult(result, jsonMode);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}
